import React, { Component } from "react";
import { getSingleShipForMeRedux } from "../../actions/index";
import { connect } from "react-redux";
import "./invoice-by-order.css";
import PaicartLogo from "../common/paicart_logo.png";
import { withRouter } from "react-router-dom";
import InvoiceShell, {
  InvoiceRow,
  InvoiceDivider,
  InvoiceSection,
  InvoiceBadge,
  InvoiceField,
  InvoiceGrid,
  InvoiceChip,
  InvoiceChipRow,
  InvoiceCartonTable,
  toInvoiceList,
  isInvoicePlaceholder,
} from "../common/invoiceShell.jsx";

/* Requests store the booked date as dd/mm/yyyy. */
const toIsoDate = (value) => {
  if (!value) return "";
  const [d, m, y] = String(value).split("/");
  return d && m && y ? `${y}-${m}-${d}` : value;
};

const tk = (amount) => `৳${parseInt(amount || 0).toLocaleString()}`;

/* The customer-facing wording for each stage, mirrored from the customer
   app's STATUS_BADGE so both invoices name a stage the same way. */
const STATUS_LABELS = {
  Pending: "Booking Confirmed",
  "Rates Provided": "Rates Provided",
  "Abroad Warehouse": "Abroad Warehouse",
  "Ready for fly": "Ready for fly",
  "Bangladesh customs": "🇧🇩 BD Customs",
  "Paicart warehouse": "Paicart WH",
  Delivered: "Delivered",
  Reject: "Rejected",
  Cancelled: "Cancelled",
};

/* A sea parcel is loaded onto a vessel rather than flown. The stored status
   stays "Ready for fly" - only what is read changes. */
const isSeaShipment = (req) =>
  String((req && (req.shipmentMethod || req.method || req.shipBy)) || "")
    .trim()
    .toLowerCase() === "sea";

const getStatusLabel = (req) => {
  if (!req) return "";
  if (req.status === "Ready for fly" && isSeaShipment(req)) {
    return "Ready for vessel";
  }
  return STATUS_LABELS[req.status] || req.status || "Pending";
};

const getTotalPaid = (req) => {
  if (!req || !req.payments || req.payments.length === 0) return 0;
  return req.payments.reduce((a, p) => a + (parseInt(p.amount) || 0), 0);
};

export class OnlyInvoieToPrint extends Component {
  state = { loading: true };

  componentDidMount = async () => {
    await this.props.getSingleShipForMeRedux(this.props.match.params.bookingId);
    this.setState({ loading: false });
  };

  render() {
    const req = this.props.orderObj;
    const bookingId = this.props.match.params.bookingId;

    const totalPaid = getTotalPaid(req);
    /* The weight × rate figure is an informational breakdown line only - the
       real bill is whatever the admin set on shippingChargeCustomer. */
    const weightRateCharge =
      req && req.receivedWeight && req.customerRate
        ? Math.round(req.receivedWeight * req.customerRate)
        : null;
    const otherCost = parseFloat(req && req.otherCost) || 0;
    const discount = parseFloat(req && req.discount) || 0;
    const refund = parseFloat(req && req.refund) || 0;
    const totalBill = parseInt(req && req.shippingChargeCustomer) || 0;
    const totalDue = totalBill - totalPaid;

    const trackingList = (
      req && req.trackingNumbers && req.trackingNumbers.length > 0
        ? toInvoiceList(req.trackingNumbers)
        : toInvoiceList(req && req.trackingNo)
    ).filter((t) => !isInvoicePlaceholder(t));
    const cartonList = toInvoiceList(req && req.ctnNo);
    const markList = toInvoiceList(req && req.shippingMark);
    // Equal lengths means they were written one-per-carton and can be paired.
    const cartonsPairWithMarks =
      cartonList.length > 0 && cartonList.length === markList.length;

    return (
      <InvoiceShell
        logo={PaicartLogo}
        docType="Shipment Invoice"
        docId={bookingId}
        docDate={toIsoDate(req && req.date)}
        loading={this.state.loading}
        notFound={!req}
      >
        {req && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 24,
              }}
            >
              <InvoiceBadge
                color={
                  req.paymentStatus === "Paid"
                    ? "emerald"
                    : req.paymentStatus === "Partially Paid"
                      ? "orange"
                      : "gray"
                }
              >
                {req.paymentStatus === "Paid"
                  ? "Paid"
                  : req.paymentStatus === "Partially Paid"
                    ? "Partially Paid"
                    : "Not Paid"}
              </InvoiceBadge>
              <InvoiceBadge color={req.status === "Reject" ? "red" : "gray"}>
                {getStatusLabel(req)}
              </InvoiceBadge>
            </div>

            <InvoiceSection title="Shipment Details">
              <InvoiceGrid>
                <InvoiceField label="Product" value={req.othersProductName} />
                <InvoiceField
                  label="Quantity"
                  value={req.productQuantity ? `${req.productQuantity} Pcs` : ""}
                />
                <InvoiceField
                  capitalize
                  label="Contains"
                  value={
                    req.productContains && req.productContains !== "none"
                      ? req.productContains
                      : "None"
                  }
                />
                <InvoiceField
                  capitalize
                  label="Method"
                  value={req.shipBy || req.method || "Air"}
                />
                <InvoiceField capitalize label="Ship From" value={req.shipFrom} />
                <InvoiceField
                  label="Booked Date"
                  value={toIsoDate(req.date)}
                />
                <InvoiceField label="Total Box" value={req.ctnQuantity} />
                <InvoiceField
                  label="Total CBM"
                  value={req.totalCbm ? `${req.totalCbm} cbm` : ""}
                />
                <InvoiceField
                  label="Actual Weight"
                  value={
                    req.receivedWeight
                      ? `${req.receivedWeight} Kg`
                      : req.weight
                        ? `${req.weight} Kg (booked)`
                        : "Pending"
                  }
                />
              </InvoiceGrid>
            </InvoiceSection>

            {trackingList.length > 0 && (
              <InvoiceSection title="Tracking">
                <div>
                  {trackingList.map((t, i) => (
                    <InvoiceChip key={i}>{t}</InvoiceChip>
                  ))}
                </div>
              </InvoiceSection>
            )}

            {/* Given their own full-width section: as grid cells these two
                multi-value fields wrapped into an unreadable block. */}
            {(cartonList.length > 0 || markList.length > 0) && (
              <InvoiceSection
                title={
                  cartonsPairWithMarks
                    ? "Cartons & Shipping Marks"
                    : "Cartons"
                }
              >
                {cartonsPairWithMarks ? (
                  <InvoiceCartonTable cartons={cartonList} marks={markList} />
                ) : (
                  <>
                    {cartonList.length > 0 && (
                      <InvoiceChipRow label="Carton No" items={cartonList} />
                    )}
                    {markList.length > 0 && (
                      <InvoiceChipRow label="Shipping Mark" items={markList} />
                    )}
                  </>
                )}
              </InvoiceSection>
            )}

            <InvoiceSection title="Payment Breakdown">
              {weightRateCharge !== null && (
                <InvoiceRow
                  label="Shipping Charge"
                  hint={`(${req.receivedWeight} Kg × ৳${req.customerRate})`}
                  value={tk(weightRateCharge)}
                />
              )}
              {otherCost > 0 && (
                <InvoiceRow
                  label="Repacking Charge"
                  value={`+${tk(otherCost)}`}
                />
              )}
              {refund > 0 && (
                <InvoiceRow
                  label="Refund"
                  value={`-${tk(refund)}`}
                  tone="green"
                />
              )}
              {discount > 0 && (
                <InvoiceRow
                  label="Discount"
                  value={`-${tk(discount)}`}
                  tone="green"
                />
              )}
              <InvoiceDivider />
              <InvoiceRow bold label="Total Due Bill" value={tk(totalBill)} />
              {totalPaid > 0 && (
                <InvoiceRow
                  label="Advance Paid"
                  value={`-${tk(totalPaid)}`}
                  tone="green"
                />
              )}
              <InvoiceDivider />
              <InvoiceRow
                bold
                label={totalDue > 0 ? "Balance Due" : "Fully Paid"}
                value={tk(totalDue > 0 ? totalDue : totalPaid)}
                tone={totalDue > 0 ? "due" : "green"}
              />
            </InvoiceSection>

            {req.payments && req.payments.length > 0 && (
              <InvoiceSection title="Payment History">
                {req.payments.map((p, i) => (
                  <InvoiceRow
                    key={i}
                    label={`${p.method || "Payment"} — ${p.date || "—"}`}
                    value={`+${tk(p.amount)}`}
                    tone="green"
                  />
                ))}
              </InvoiceSection>
            )}
          </>
        )}
      </InvoiceShell>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    orderObj: state.productRequests.shipForMeObj,
  };
};

export default withRouter(
  connect(mapStateToProps, {
    getSingleShipForMeRedux,
  })(OnlyInvoieToPrint),
);
